import { createClient, type Session, type SupabaseClient } from '@supabase/supabase-js';
import type { Identity } from './types';

/** The anon key is public by design — row level security protects the data. */
const url = import.meta.env.VITE_SUPABASE_URL || 'https://frnlloffzfnohagpwsti.supabase.co';
const anonKey =
  import.meta.env.VITE_SUPABASE_ANON_KEY ||
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImZybmxsb2ZmemZub2hhZ3B3c3RpIiwicm9sZSI6ImFub24iLCJpYXQiOjE3ODcxNzcwMTMsImV4cCI6MjEwMjc1MzAxM30.HJLWJm_2YPvUvwcWXpTLVM9rGmfYEhivBxHt1dYx0-g';

export const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://frameon.us';

let client: SupabaseClient | null = null;

export function supabase(): SupabaseClient {
  // The handoff tokens arrive in the fragment and are consumed by hand in the
  // verify route, so the client must not race us for them.
  client ??= createClient(url, anonKey, {
    auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: false, flowType: 'pkce' }
  });
  return client;
}

export async function currentSession(): Promise<Session | null> {
  const { data, error } = await supabase().auth.getSession();
  if (error) return null;
  return data.session;
}

/**
 * Sign out everywhere, not just this tab. `scope: 'global'` revokes the refresh
 * token server-side, so the copy the signup page still holds on the other
 * origin stops working too.
 */
export async function signOutSupabase(): Promise<void> {
  await supabase().auth.signOut({ scope: 'global' }).catch(() => undefined);
}

/**
 * Where to send someone to sign in.
 *
 * `/signin` takes both — Google creates the account when there isn't one — so
 * there is no separate signup destination to choose between.
 *
 * `signedout=1` matters: frameon.us and this origin cannot share
 * storage, so without it the sign-in page still holds a session, silently
 * bounces them back here, and nobody can ever sign out or switch account.
 */
export function signInUrl(afterSignOut = false): string {
  return `${SITE_URL}/signin${afterSignOut ? '?signedout=1' : ''}`;
}

export function identityOf(session: Session): Identity {
  const user = session.user;
  const meta = (user.user_metadata || {}) as Record<string, string>;
  return {
    id: user.id,
    email: user.email || '',
    name: meta.full_name || meta.name || (user.email || '').split('@')[0] || 'Producer',
    avatarUrl: meta.avatar_url || meta.picture || null
  };
}

/**
 * Mirrors the signed-in identity into `profiles`.
 *
 * Only these columns: PostgREST rejects the whole row if any named column is
 * missing, and the live table has drifted from the migrations in this repo.
 * Creating the row also needs the INSERT policy in
 * `supabase/migrations/20260905090000_profiles_insert_policy.sql`.
 */
export async function syncSupabaseProfile(session: Session): Promise<void> {
  const who = identityOf(session);
  const { error } = await supabase()
    .from('profiles')
    .upsert({ id: who.id, email: who.email, full_name: who.name, avatar_url: who.avatarUrl }, { onConflict: 'id' });
  if (error) throw new Error(error.message);
}
